import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Circle, Group, Image, Label, Line, Rect, Tag, Text } from 'react-konva';
import { useImage } from 'react-konva-utils';
import { KonvaEventObject } from 'konva/lib/Node';
import { Text as TextType } from 'konva/lib/shapes/Text';
import { useTheme } from 'styled-components';

import BurgerIcon from '@/assets/icons/burger-white.svg';
import { DEFAULT_NODE_RADIUS } from '@/features/canvas/constants';
import { BudgetViewTab, ViewMode } from '@/features/canvas/contexts/editor-context';
import { InitiativeBudgetData } from '@/features/canvas/types/budget-type';
import { getNodeRadius } from '@/features/canvas/utils/get-node-radius';
import { needsTruncation, truncateText } from '@/features/canvas/utils/text-utils';
import { components } from '@/types/api';

const formatCurrency = (value: number, currency: string): string => {
  let symbol = '£';
  switch (currency) {
    case 'USD':
      symbol = '$';
      break;
    case 'EUR':
      symbol = '€';
      break;
    case 'AUD':
      symbol = 'A$';
      break;
    case 'CAD':
      symbol = 'C$';
      break;
    case 'AED':
      symbol = 'د.إ';
      break;
    case 'JPY':
      symbol = '¥';
      break;
    case 'CNY':
      symbol = '¥';
      break;
    case 'DKK':
      symbol = 'kr';
      break;
    case 'HKD':
      symbol = 'HK$';
      break;
    case 'INR':
      symbol = '₹';
      break;
    case 'NOK':
      symbol = 'kr';
      break;
    case 'QAR':
      symbol = 'ر.ق';
      break;
    case 'SAR':
      symbol = 'ر.س';
      break;
    case 'SGD':
      symbol = 'S$';
      break;
    case 'SEK':
      symbol = 'kr';
      break;
    case 'ZAR':
      symbol = 'R';
      break;
    case 'PKR':
      symbol = '₨';
      break;
    case 'NZD':
      symbol = 'NZ$';
      break;
    default:
      symbol = '£';
  }

  return `${symbol}${value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
};

const getBudgetText = (
  budgetData: InitiativeBudgetData | undefined,
  viewMode: ViewMode,
  budgetViewTab: BudgetViewTab,
  isLoadingBudget: boolean
): string => {
  if (!budgetData || viewMode !== 'budget' || isLoadingBudget) return '';

  if (budgetViewTab === 'total') {
    return formatCurrency(budgetData.total_budget, budgetData.currency);
  }

  if (budgetViewTab === 'capex-opex') {
    const capex = budgetData.capex || 0;
    const opex = budgetData.opex || 0;
    const total = capex + opex;

    if (total === 0) {
      return '0% C | 0% O';
    }

    const capexPercentage = Math.round((capex / total) * 100);
    const opexPercentage = Math.round((opex / total) * 100);

    return `${capexPercentage}% C | ${opexPercentage}% O`;
  }

  if (budgetViewTab === 'spend') {
    // If there's no budget, show 0% remaining
    if (budgetData.total_budget === 0) {
      return '0% R';
    }

    const percentage = (budgetData.spend / budgetData.total_budget) * 100;
    const remainingPercentage = 100 - percentage;
    return `${remainingPercentage.toFixed(0)}% R`;
  }

  return '';
};

const getWordBoundaryLine = (text: string, maxLength: number): string => {
  const line = text.slice(0, maxLength);
  const isCuttingMidWord = /\w$/.test(line) && /^\w/.test(text.slice(maxLength, maxLength + 1));

  if (isCuttingMidWord) {
    const lastSpace = line.lastIndexOf(' ');
    return lastSpace > 0 ? line.slice(0, lastSpace) : line;
  }

  return line;
};

const renderCrossOverlay = (radius: number) => (
  <>
    <Line
      points={[-radius / 3, -radius / 3, radius / 3, radius / 3]}
      stroke="#ffffff"
      strokeWidth={2}
      lineCap="round"
    />
    <Line
      points={[-radius / 3, radius / 3, radius / 3, -radius / 3]}
      stroke="#ffffff"
      strokeWidth={2}
      lineCap="round"
    />
  </>
);

type ShapeProps = {
  disabled: boolean;
  id: number;
  isToggledOff: boolean;
  onMouseEnter: (e: KonvaEventObject<MouseEvent>) => void;
  onMouseLeave: (e: KonvaEventObject<MouseEvent>) => void;
  shape: 'circle' | 'square' | 'triangle';
  viewMode: ViewMode;
  fill?: string;
  finalColor?: string;
  finalOpacity?: number;
  isHovered?: boolean;
  opacity?: number;
  shouldHighlight?: boolean;
  size?: number;
};

const ShapeRenderer = ({
  id,
  shape,
  size,
  fill,
  opacity,
  isToggledOff,
  isHovered = false,
  viewMode,
  disabled,
  shouldHighlight = false,
  finalColor,
  finalOpacity,
  onMouseEnter,
  onMouseLeave,
}: ShapeProps) => {
  const trianglePoints = [0, 20, 10, 0, 20, 20];
  const radius = getNodeRadius(size);

  const shouldShowCross = viewMode === 'budget' && isHovered && !disabled && !isToggledOff;

  switch (shape) {
    case 'triangle':
      return (
        <Group id={id.toString()} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
          <Line
            x={-DEFAULT_NODE_RADIUS}
            y={-DEFAULT_NODE_RADIUS}
            points={trianglePoints}
            closed
            fill={isToggledOff ? 'transparent' : fill}
            opacity={opacity}
            stroke={isToggledOff ? fill : undefined}
            strokeWidth={isToggledOff ? 2 : 0}
          />
          {shouldShowCross && renderCrossOverlay(radius)}
        </Group>
      );

    case 'square':
      return (
        <Group id={id.toString()} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
          <Rect
            x={-DEFAULT_NODE_RADIUS}
            y={-DEFAULT_NODE_RADIUS}
            width={DEFAULT_NODE_RADIUS * 2}
            height={DEFAULT_NODE_RADIUS * 2}
            fill={isToggledOff ? 'transparent' : fill}
            opacity={opacity}
            stroke={isToggledOff ? fill : undefined}
            strokeWidth={isToggledOff ? 2 : 0}
          />
          {shouldShowCross && renderCrossOverlay(radius)}
        </Group>
      );

    case 'circle':
    default: {
      const content =
        shouldHighlight && viewMode !== 'budget' ? (
          <>
            <Circle x={0} y={0} radius={radius} fill={finalColor} opacity={finalOpacity} />
            <Circle x={0} y={0} radius={radius * (11 / 19)} fill="#ffffff" opacity={finalOpacity} />
            <Circle
              x={0}
              y={0}
              radius={radius * (9 / 19)}
              fill={finalColor}
              opacity={finalOpacity}
            />
          </>
        ) : (
          <>
            <Circle
              x={0}
              y={0}
              radius={radius}
              fill={isToggledOff ? 'transparent' : fill}
              opacity={opacity}
              stroke={isToggledOff ? fill : undefined}
              strokeWidth={isToggledOff ? 2 : 0}
            />
            {shouldShowCross && renderCrossOverlay(radius)}
          </>
        );

      return (
        <Group id={id.toString()} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
          {content}
        </Group>
      );
    }
  }
};

const setCursor = (e: KonvaEventObject<MouseEvent>, value: 'pointer' | 'default') => {
  const stage = e.target.getStage();
  if (stage) stage.container().style.cursor = value;
};

const createMouseHandlers = (
  disabled: boolean,
  onMouseEnter?: (e: KonvaEventObject<MouseEvent>) => void,
  onMouseLeave?: (e: KonvaEventObject<MouseEvent>) => void,
  setTextHovered?: (value: boolean) => void
) => {
  const handleShapeMouseEnter = (e: KonvaEventObject<MouseEvent>) => {
    if (disabled) return;
    setCursor(e, 'pointer');
    onMouseEnter?.(e);
  };

  const handleShapeMouseLeave = (e: KonvaEventObject<MouseEvent>) => {
    if (disabled) return;
    setCursor(e, 'default');
    onMouseLeave?.(e);
  };

  const handleTextMouseEnter = (e: KonvaEventObject<MouseEvent>) => {
    if (disabled) return;
    setTextHovered?.(true);
    setCursor(e, 'default');
  };

  const handleTextMouseLeave = (e: KonvaEventObject<MouseEvent>) => {
    if (disabled) return;
    setTextHovered?.(false);
    setCursor(e, 'default');
  };

  return {
    handleShapeMouseEnter,
    handleShapeMouseLeave,
    handleTextMouseEnter,
    handleTextMouseLeave,
  };
};

type FinalStylesOptions = {
  disabled: boolean;
  isHovered: boolean;
  textHovered: boolean;
  fill?: string;
  opacity?: number;
  theme?: { palette?: { brand?: { editorTabPrimary?: string } } };
};

const calculateFinalStyles = ({
  disabled,
  textHovered,
  isHovered,
  opacity,
  fill,
  theme,
}: FinalStylesOptions) => {
  let finalOpacity = opacity;
  let finalColor = fill;
  let finalTextColor = '#6e6e72';
  let textDecoration: 'underline' | undefined;

  if (!disabled) {
    if (textHovered) {
      finalTextColor = '#2cbef4';
      textDecoration = 'underline';
      finalOpacity = 1;
    } else if (isHovered) {
      finalOpacity = 1;
      finalColor = theme?.palette?.brand?.editorTabPrimary || finalColor;
    }
  }

  return {
    finalOpacity,
    finalColor,
    finalTextColor,
    textDecoration,
  };
};

const calculateBudgetMetrics = (
  budgetData?: InitiativeBudgetData,
  budgetViewTab?: BudgetViewTab
) => {
  if (!budgetData) {
    return { spendPercentage: 0, remainingPercentage: 0, isOverspent: false };
  }

  const spendPercentage = (() => {
    if (budgetViewTab === 'spend') {
      if (budgetData.total_budget === 0) {
        return 0;
      }
      return (budgetData.spend / budgetData.total_budget) * 100;
    }
    return 0;
  })();

  const remainingPercentage = (() => {
    if (budgetViewTab === 'spend' && budgetData.total_budget === 0) {
      return 0;
    }
    return 100 - spendPercentage;
  })();

  const isOverspent = budgetViewTab === 'spend' && remainingPercentage < 0;

  return { spendPercentage, remainingPercentage, isOverspent };
};

const hasInitiativeRelationships = (
  initiative?: components['schemas']['Initiative'] & {
    children?: { id: number; title: string }[];
    parents?: { id: number; title: string }[];
  }
): boolean => {
  return !!(
    (initiative?.children && initiative.children.length > 0) ||
    (initiative?.parents && initiative.parents.length > 0)
  );
};

type NodeShapeProps = {
  id: number;
  shape: 'circle' | 'square' | 'triangle';
  showDependencies: boolean;
  title: string;
  x: number;
  y: number;
  active?: boolean;
  budgetData?: InitiativeBudgetData;
  budgetViewTab?: BudgetViewTab;
  disabled?: boolean;
  draggable?: boolean;
  fill?: string;
  initiative?: components['schemas']['Initiative'] & {
    children?: { id: number; title: string }[];
    parents?: { id: number; title: string }[];
  };
  isHovered?: boolean;
  isLoadingBudget?: boolean;
  isToggledOff?: boolean;
  onClick?: (evt: KonvaEventObject<MouseEvent>) => void;
  onDblClick?: (evt: KonvaEventObject<MouseEvent>) => void;
  onDragEnd?: (evt: KonvaEventObject<DragEvent>) => void;
  onDragMove?: (evt: KonvaEventObject<DragEvent>) => void;
  onMouseEnter?: (e: KonvaEventObject<MouseEvent>) => void;
  onMouseLeave?: (e: KonvaEventObject<MouseEvent>) => void;
  opacity?: number;
  size?: number;
  viewMode?: ViewMode;
};

const NodeShape = React.memo(
  ({
    id,
    title,
    x,
    y,
    fill,
    size,
    active = false,
    onDragMove,
    onDragEnd,
    onClick,
    onDblClick,
    draggable,
    shape,
    opacity,
    showDependencies,
    onMouseEnter,
    onMouseLeave,
    isHovered,
    disabled = false,
    viewMode = 'filter',
    budgetViewTab = 'total',
    budgetData,
    isLoadingBudget = false,
    isToggledOff = false,
    initiative,
  }: NodeShapeProps) => {
    const theme = useTheme();
    const [burgerIcon] = useImage(BurgerIcon);

    const [augmentedTitle, setAugmentedTitle] = useState('');
    const [displayTitle, setDisplayTitle] = useState('');
    const [textWidth, setTextWidth] = useState(0);
    const [isTextMultiline, setIsTextMultiline] = useState(false);
    const [textHovered, setTextHovered] = useState(false);
    const [showFullTitle, setShowFullTitle] = useState(false);

    const shouldHighlight = showDependencies && isHovered;

    useEffect(() => {
      if (title !== augmentedTitle || viewMode) {
        const cleanedTitle = title.replace(/\s+/g, ' ');
        setAugmentedTitle(cleanedTitle);

        let displayText: string;
        let isMultiline = false;

        if (viewMode === 'budget') {
          // Budget mode: single line, 32 chars + ...
          displayText = truncateText(cleanedTitle, 32);
        } else if (cleanedTitle.length <= 20) {
          // Filter mode: fits on first line
          displayText = cleanedTitle;
        } else if (cleanedTitle.length <= 32) {
          // Filter mode: split across two lines: 20 chars + remaining up to 12 chars
          const firstLine = getWordBoundaryLine(cleanedTitle, 20);
          const secondLine = cleanedTitle.slice(firstLine.length).trim();
          displayText = `${firstLine}\n${secondLine}`;
          isMultiline = true;
        } else {
          // Filter mode: split across two lines: 20 chars + 12 chars + ...
          const firstLine = getWordBoundaryLine(cleanedTitle, 20);
          const remainingText = cleanedTitle.slice(firstLine.length).trim();

          const secondLine =
            remainingText.length > 12 ? `${remainingText.slice(0, 12)}...` : remainingText;
          displayText = `${firstLine}\n${secondLine}`;
          isMultiline = true;
        }

        setDisplayTitle(displayText);
        setIsTextMultiline(isMultiline);
        // Show full title on hover if it needs truncation
        setShowFullTitle(needsTruncation(cleanedTitle, 32));
      }
    }, [title, augmentedTitle, viewMode]);

    useLayoutEffect(() => {
      if (isTextMultiline) {
        // For multiline text, set a fixed width that accommodates the longest expected line (20 chars)
        const sampleText = new TextType({
          text: 'A'.repeat(20), // 20 characters for the first line
          fontSize: 16,
          fontStyle: active ? 'bold' : 'normal',
        });
        setTextWidth(sampleText.width());
        sampleText.destroy();
      } else {
        // For single-line text, calculate based on actual text
        const testText = new TextType({
          text: displayTitle,
          fontSize: 16,
          fontStyle: active ? 'bold' : 'normal',
        });
        setTextWidth(testText.width());
        testText.destroy();
      }
    }, [displayTitle, active, isTextMultiline]);

    const { finalOpacity, finalColor, finalTextColor, textDecoration } = calculateFinalStyles({
      disabled,
      textHovered,
      isHovered: isHovered || false,
      opacity,
      fill,
      theme,
    });

    const {
      handleShapeMouseEnter,
      handleShapeMouseLeave,
      handleTextMouseEnter,
      handleTextMouseLeave,
    } = createMouseHandlers(disabled, onMouseEnter, onMouseLeave, setTextHovered);

    const handleDblClick = disabled
      ? undefined
      : (e: KonvaEventObject<MouseEvent>) => {
          if (e.target.getClassName() === 'Text') onDblClick?.(e);
        };

    // Calculate the actual radius for icon positioning
    const nodeRadius = getNodeRadius(size);

    const budgetText = getBudgetText(budgetData, viewMode, budgetViewTab, isLoadingBudget);
    const titleYOffset = isTextMultiline ? -nodeRadius : -6;
    const budgetTextYOffset = isTextMultiline ? titleYOffset + 35 : titleYOffset + 20;

    const { remainingPercentage, isOverspent } = calculateBudgetMetrics(budgetData, budgetViewTab);

    const shouldShowBudget = viewMode === 'budget' && !isToggledOff && opacity === 1;

    // Determine if the relationship indicator should be shown
    const hasRelationships = hasInitiativeRelationships(initiative);

    return (
      <Group
        id={id.toString()}
        x={x}
        y={y}
        draggable={draggable && !disabled}
        listening={!disabled}
        onDragMove={disabled ? undefined : onDragMove}
        onDragEnd={disabled ? undefined : onDragEnd}
        onClick={disabled ? undefined : onClick}
        onDblClick={handleDblClick}
        onTap={onClick}
        onDblTap={onDblClick}
      >
        <ShapeRenderer
          id={id}
          shape={shape}
          size={size}
          fill={fill}
          opacity={opacity}
          isToggledOff={isToggledOff}
          isHovered={isHovered}
          viewMode={viewMode}
          disabled={disabled}
          shouldHighlight={shouldHighlight}
          finalColor={finalColor}
          finalOpacity={finalOpacity}
          onMouseEnter={handleShapeMouseEnter}
          onMouseLeave={handleShapeMouseLeave}
        />
        {/* Relationship indicator icon */}
        {hasRelationships && burgerIcon && (
          <Image
            image={burgerIcon}
            x={nodeRadius - nodeRadius * 1.5}
            y={-nodeRadius + nodeRadius * 0.5}
            width={nodeRadius}
            height={nodeRadius}
            opacity={finalOpacity}
            listening={false}
          />
        )}
        <Text
          width={textWidth}
          wrap="word"
          fontSize={16}
          fontStyle={active ? 'bold' : 'normal'}
          x={nodeRadius + 8}
          y={titleYOffset}
          text={displayTitle}
          opacity={finalOpacity}
          fill={finalTextColor}
          textDecoration={textDecoration}
          onMouseEnter={handleTextMouseEnter}
          onMouseLeave={handleTextMouseLeave}
        />
        {textHovered &&
          showFullTitle &&
          (() => {
            // Calculate tooltip dimensions
            const tooltipWidth = Math.min(320, Math.max(200, augmentedTitle.length * 7.5));
            const padding = 6;
            const fontSize = 11;
            const lineHeight = 1.3;

            // Estimate number of lines needed
            const approximateCharsPerLine = (tooltipWidth - padding * 2) / (fontSize * 0.6);
            const estimatedLines = Math.ceil(augmentedTitle.length / approximateCharsPerLine);
            const tooltipHeight = estimatedLines * fontSize * lineHeight + padding * 2;

            // Position tooltip 10px above title, anchored from bottom
            const tooltipY = titleYOffset - 10;

            return (
              <Label
                x={nodeRadius + 8}
                y={tooltipY}
                offsetY={tooltipHeight}
                opacity={1}
                listening={false}
              >
                <Tag
                  fill="rgba(97, 97, 97, 0.92)"
                  cornerRadius={4}
                  shadowColor="rgba(0, 0, 0, 0.12)"
                  shadowBlur={3}
                  shadowOffset={{ x: 0, y: 1 }}
                  shadowOpacity={1}
                />
                <Text
                  text={augmentedTitle}
                  fontSize={fontSize}
                  fontFamily="Roboto, Helvetica, Arial, sans-serif"
                  fill="#ffffff"
                  padding={padding}
                  width={tooltipWidth}
                  wrap="word"
                  align="left"
                  lineHeight={lineHeight}
                />
              </Label>
            );
          })()}
        {shouldShowBudget && budgetText && (
          <>
            {budgetViewTab === 'total' && (
              <Text
                fontSize={16}
                fontStyle="bold"
                x={nodeRadius + 8}
                y={budgetTextYOffset}
                text={budgetText}
                opacity={finalOpacity}
                fill={theme.palette.brand.editorTabPrimary}
              />
            )}
            {budgetViewTab === 'capex-opex' && (
              <Group>
                {(() => {
                  const parts = budgetText.split(' ');
                  const capexPart = `${parts[0]} ${parts[1]}`;
                  const divider = parts[2];
                  const opexPart = `${parts[3]} ${parts[4]}`;

                  // Calculate text widths dynamically
                  const capexText = new TextType({
                    text: capexPart,
                    fontSize: 16,
                    fontStyle: 'bold',
                  });
                  const dividerText = new TextType({
                    text: divider,
                    fontSize: 16,
                    fontStyle: 'bold',
                  });

                  const capexWidth = capexText.width();
                  const dividerWidth = dividerText.width();

                  capexText.destroy();
                  dividerText.destroy();

                  const baseX = nodeRadius + 8;
                  const dividerX = baseX + capexWidth + 5;
                  const opexX = dividerX + dividerWidth + 5;

                  return (
                    <>
                      <Text
                        fontSize={16}
                        fontStyle="bold"
                        x={baseX}
                        y={budgetTextYOffset}
                        text={capexPart}
                        opacity={finalOpacity}
                        fill={theme.palette.brand.editorTabPrimary}
                      />
                      <Text
                        fontSize={16}
                        fontStyle="bold"
                        x={dividerX}
                        y={budgetTextYOffset}
                        text={divider}
                        opacity={finalOpacity}
                        fill="#363648"
                      />
                      <Text
                        fontSize={16}
                        fontStyle="bold"
                        x={opexX}
                        y={budgetTextYOffset}
                        text={opexPart}
                        opacity={finalOpacity}
                        fill="#a5a5a5"
                      />
                    </>
                  );
                })()}
              </Group>
            )}
            {budgetViewTab === 'spend' && (
              <>
                <Text
                  fontSize={15}
                  fontStyle="bold"
                  x={nodeRadius + 8}
                  y={budgetTextYOffset}
                  text={budgetText}
                  opacity={finalOpacity}
                  fill={isOverspent ? '#c92c2d' : theme.palette.brand.editorTabPrimary}
                />
                {/* Progress bar with dynamic positioning */}
                {(() => {
                  // Calculate text width dynamically
                  const budgetTextObj = new TextType({
                    text: budgetText,
                    fontSize: 15,
                    fontStyle: 'bold',
                  });
                  const budgetTextWidth = budgetTextObj.width();
                  budgetTextObj.destroy();

                  // Add padding between text and progress bar
                  const progressBarX = nodeRadius + 8 + budgetTextWidth + 6;

                  return (
                    <>
                      {/* Progress bar background with border */}
                      <Rect
                        x={progressBarX}
                        y={budgetTextYOffset + 4.5}
                        width={60}
                        height={6}
                        fill="#fff"
                        stroke={isOverspent ? '#c92c2d' : '#e0e0e0'}
                        strokeWidth={1}
                        cornerRadius={3}
                        opacity={finalOpacity}
                      />
                      {/* Progress bar fill group */}
                      <Group>
                        {/* Main fill with rounded corners */}
                        <Rect
                          x={progressBarX}
                          y={budgetTextYOffset + 4.5}
                          width={60 * (Math.max(0, remainingPercentage) / 100)}
                          height={6}
                          fill={theme.palette.brand.editorTabPrimary}
                          cornerRadius={3}
                          opacity={finalOpacity}
                        />
                        {/* Rectangle to square off the right side */}
                        {remainingPercentage > 0 && remainingPercentage < 100 && (
                          <Rect
                            x={progressBarX + 60 * (Math.max(0, remainingPercentage) / 100) - 3}
                            y={budgetTextYOffset + 4.5}
                            width={3}
                            height={6}
                            fill={theme.palette.brand.editorTabPrimary}
                            opacity={finalOpacity}
                          />
                        )}
                      </Group>
                    </>
                  );
                })()}
              </>
            )}
          </>
        )}
      </Group>
    );
  }
);

export { NodeShape };
